import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';

import { Box, Divider, Flex, Link, ListItem, OrderedList, Text, UnorderedList } from '@bonusly/design-system';

import { splitSources } from '../lib/split-sources';

interface BizyStreamingMessageProps {
	/** The content to render */
	content: string;
	/** Whether the response is complete (switches from thinking style to final style) */
	isComplete?: boolean;
	/** Called when content updates (for auto-scroll) */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running (e.g., ["Reading your notes...", "Working on recognition..."]) */
	toolProgress?: string[];
}

/**
 * Renders Bizy content as direct markdown (no character-by-character animation).
 *
 * Used by the V2 streaming component for BOTH in-progress and completed messages
 * to avoid the jarring flash that occurs when switching between different component
 * types on completion. The same component handles both states:
 *
 * - Streaming (isComplete=false): italic text, "Thinking..." label, pulsing cursor
 * - Complete (isComplete=true): normal text, no label, no cursor
 */
export function BizyStreamingMessage({ content, isComplete = false, onContentUpdate, toolProgress }: BizyStreamingMessageProps) {
	const { t } = useTranslation('admin_bizy');
	const prevContentLengthRef = useRef(content.length);

	useEffect(() => {
		if (content.length !== prevContentLengthRef.current) {
			prevContentLengthRef.current = content.length;
			onContentUpdate?.();
		}
	}, [content, onContentUpdate]);

	const textColor = isComplete ? 'text.default' : 'text.softer';
	const { body, sources } = useMemo(() => splitSources(content), [content]);

	const markdownSx = {
		'p + p': { marginTop: '0.5em' },
		'ul, ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '4px' },
		'li + li': { marginTop: '8px' },
		'p:first-of-type': { marginTop: 0 },
		'p:last-of-type': { marginBottom: 0 },
		hr: { marginTop: '0.75em', marginBottom: '0.5em', borderColor: 'border.soft' },
	};

	const markdownComponents = {
		a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
			<Link href={href} color="text.interactive" target="_blank" rel="noopener noreferrer">
				{children}
			</Link>
		),
		p: ({ children }: { children?: React.ReactNode }) => (
			<Text fontSize="sm" color={textColor}>
				{children}
			</Text>
		),
		/* eslint-disable @typescript-eslint/no-unused-vars */
		h1: ({ children }: { children?: React.ReactNode }) => (
			<Text fontSize="md" fontWeight="semibold" color={textColor}>
				{children}
			</Text>
		),
		h2: ({ children }: { children?: React.ReactNode }) => (
			<Text fontSize="md" fontWeight="semibold" color={textColor}>
				{children}
			</Text>
		),
		h3: ({ children }: { children?: React.ReactNode }) => (
			<Text fontSize="md" fontWeight="semibold" color={textColor}>
				{children}
			</Text>
		),
		strong: ({ children }: { children?: React.ReactNode }) => (
			<Text as="strong" fontSize="inherit" fontWeight="semibold" color="inherit" display="inline">
				{children}
			</Text>
		),
		ol: ({ ordered, ...props }: { ordered?: boolean; [key: string]: unknown }) => <OrderedList fontSize="sm" color={textColor} {...props} />,
		ul: ({ ordered, ...props }: { ordered?: boolean; [key: string]: unknown }) => <UnorderedList fontSize="sm" color={textColor} {...props} />,
		li: ({ ordered, ...props }: { ordered?: boolean; [key: string]: unknown }) => <ListItem {...props} />,
		/* eslint-enable @typescript-eslint/no-unused-vars */
		pre: ({ children }: { children?: React.ReactNode }) => <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{children}</pre>,
		code: ({ children }: { children?: React.ReactNode }) => (
			<Text as="code" fontSize="sm" bg="bg.muted" px={1} borderRadius="sm">
				{children}
			</Text>
		),
	};

	return (
		<Box alignSelf="flex-start" maxWidth="100%" pl={1} pr={3}>
			{/* Progress labels for streaming state — shows accumulated tool progress or "Thinking..." */}
			{!isComplete && (
				<Flex direction="column" gap={0.5} mb={1}>
					{toolProgress && toolProgress.length > 0 ? (
						toolProgress.map((item, index) => {
							const isLatest = index === toolProgress.length - 1;
							return (
								<Text
									key={index}
									fontSize="xs"
									fontWeight="medium"
									color="text.softest"
									sx={
										isLatest
											? {
													'@keyframes pulse': {
														'0%, 100%': { opacity: 1 },
														'50%': { opacity: 0.4 },
													},
													animation: 'pulse 1.5s ease-in-out infinite',
												}
											: { opacity: 0.6 }
									}
								>
									{item}
								</Text>
							);
						})
					) : (
						<Text
							fontSize="xs"
							fontWeight="medium"
							color="text.softest"
							sx={{
								'@keyframes pulse': {
									'0%, 100%': { opacity: 1 },
									'50%': { opacity: 0.4 },
								},
								animation: 'pulse 1.5s ease-in-out infinite',
							}}
						>
							{t('thinking')}
						</Text>
					)}
				</Flex>
			)}

			<Box color={textColor} sx={markdownSx}>
				<Markdown components={markdownComponents}>{body}</Markdown>
			</Box>

			{sources && (
				<Box mt={3}>
					<Divider borderColor="border.soft" />
					<Box mt={2} color="text.softer" fontStyle="normal" sx={{ 'p:first-of-type': { marginTop: 0 }, 'p:last-of-type': { marginBottom: 0 } }}>
						<Markdown
							components={{
								a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
									<Link href={href} color="text.interactive" fontSize="xs" target="_blank" rel="noopener noreferrer">
										{children}
									</Link>
								),
								p: ({ children }: { children?: React.ReactNode }) => (
									<Text fontSize="xs" color="text.softer">
										{children}
									</Text>
								),
							}}
						>
							{sources}
						</Markdown>
					</Box>
				</Box>
			)}
		</Box>
	);
}
